import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { Layout } from './components/common/Layout';
import { MobileLayout } from './components/common/MobileLayout';
import { PageLoading } from './components/common/PageLoading';
import { ProfitRouteGuard } from './hooks/useRouteGuard';
import { Suspense, lazy } from 'react';
import { Role } from '@shared/types';

// 懒加载页面组件
const LoginPage = lazy(() => import('./pages/admin/LoginPage'));
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const RealnamePage = lazy(() => import('./pages/admin/RealnamePage'));
const CommissionPage = lazy(() => import('./pages/admin/CommissionPage'));
const CardPage = lazy(() => import('./pages/admin/CardPage'));
const AgentPage = lazy(() => import('./pages/admin/AgentPage'));
const OcrSettingsPage = lazy(() => import('./pages/admin/OcrSettingsPage'));
const CardTypePage = lazy(() => import('./pages/admin/CardTypePage'));

const MobileLoginPage = lazy(() => import('./pages/mobile/MobileLoginPage'));
const MobileHomePage = lazy(() => import('./pages/mobile/MobileHomePage'));
const MobileRealnamePage = lazy(() => import('./pages/mobile/MobileRealnamePage'));
const MobileRecordsPage = lazy(() => import('./pages/mobile/MobileRecordsPage'));
const MobileCommissionPage = lazy(() => import('./pages/mobile/MobileCommissionPage'));
const MobileProfilePage = lazy(() => import('./pages/mobile/MobileProfilePage'));
const MobileSubordinatesPage = lazy(() => import('./pages/mobile/MobileSubordinatesPage'));

/**
 * 根组件：路由配置
 * - /login 通用登录页
 * - /admin/* 管理端（需admin角色）
 * - /m/* 手机端（需agent角色）
 */
function App() {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);

  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* 通用登录页 */}
        <Route path="/login" element={<LoginPage />} />

        {/* 管理端路由（Layout 包裹侧边栏+顶栏） */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute requireRole={Role.ADMIN}>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="realname" element={<RealnamePage />} />
          <Route path="commission" element={<CommissionPage />} />
          <Route path="cards" element={<CardPage />} />
          <Route path="card-types" element={<CardTypePage />} />
          <Route path="agents" element={<AgentPage />} />
          <Route path="ocr-settings" element={<OcrSettingsPage />} />
        </Route>

        {/* 手机端路由 */}
        <Route path="/m/login" element={<MobileLoginPage />} />
        <Route
          path="/m"
          element={
            <ProtectedRoute requireRole={Role.AGENT}>
              <MobileLayout />
            </ProtectedRoute>
          }
        >
          <Route path="home" element={<MobileHomePage />} />
          <Route path="realname" element={<MobileRealnamePage />} />
          <Route path="records" element={<MobileRecordsPage />} />
          {/* 分账明细页：三级代理（profit_visible=false）访问被重定向到 /m/home 并弹提示（V2-04） */}
          <Route
            path="commission"
            element={
              <ProfitRouteGuard>
                <MobileCommissionPage />
              </ProfitRouteGuard>
            }
          />
          <Route path="profile" element={<MobileProfilePage />} />
          <Route path="subordinates" element={<MobileSubordinatesPage />} />
        </Route>

        {/* 根路径重定向：已登录按角色跳转，未登录跳登录页 */}
        <Route
          path="/"
          element={
            token ? (
              <Navigate to={user?.role === 'admin' ? '/admin/dashboard' : '/m/home'} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        {/* 兜底重定向 */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
